"use client";
import { Api } from "@/Apis";
import { Format } from "@/Components";
import { useT } from "@/Hooks";
import { CheckCircleFilled, CloseOutlined, CustomerServiceOutlined, MessageOutlined, SendOutlined } from "@ant-design/icons";
import { HubConnectionBuilder, LogLevel } from "@microsoft/signalr";
import { Avatar, Badge, Button, Card, Empty, Flex, FloatButton, Form, Input, List, Typography, theme } from "antd";
import { useEffect, useMemo, useRef, useState } from "react";

interface LandingConversationSession {
  ConversationId: number;
  SessionToken: string;
}

export default function LandingConversationFloat({ targetCenterId }: { targetCenterId?: number }) {
  const t = useT();
  const { token } = theme.useToken();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<{ SenderName: string; Text: string; DateRegister?: string; isOptimistic?: boolean }[]>([]);
  const [sessionVersion, setSessionVersion] = useState(0);
  const [sending, setSending] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [form] = Form.useForm<{ Text: string }>();
  const MessagesEndRef = useRef<HTMLDivElement>(null);
  const key = `landingConversation:${targetCenterId ?? "domain"}`;
  const operatorName = useMemo(() => [...messages].reverse().find((message) => message.SenderName !== "Visitor")?.SenderName, [messages]);

  const getSession = (): LandingConversationSession | undefined => {
    const raw = sessionStorage.getItem(key);
    return raw ? JSON.parse(raw) as LandingConversationSession : undefined;
  };
  const load = async () => {
    const session = getSession();
    if (!session) return;
    const result = await Api.LandingConversation.detail(session);
    setMessages(result.Result?.Messages ?? []);
  };
  useEffect(() => {
    if (open) void load();
  }, [open, sessionVersion]);
  useEffect(() => {
    MessagesEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages.length]);
  useEffect(() => {
    const session = getSession();
    if (!session) return;
    const url = `${Api.BaseUrl}/hubs/landing-conversation?conversationId=${session.ConversationId}&sessionToken=${encodeURIComponent(session.SessionToken)}`;
    const connection = new HubConnectionBuilder().withUrl(url).configureLogging(LogLevel.Warning).withAutomaticReconnect().build();
    connection.on("ConversationChanged", () => {
      if (!open) setUnreadCount((count) => count + 1);
      void load();
    });
    void connection.start();
    return () => { connection.off("ConversationChanged"); void connection.stop(); };
  }, [open, sessionVersion]);

  const send = async ({ Text }: { Text: string }) => {
    const temporaryMessage = { SenderName: "Visitor", Text, DateRegister: new Date().toISOString(), isOptimistic: true };
    setMessages((current) => [...current, temporaryMessage]);
    form.resetFields();
    setSending(true);
    try {
      const session = getSession();
      const result = session
        ? await Api.LandingConversation.send({ ...session, Text })
        : await Api.LandingConversation.start({
          TargetCenterId: targetCenterId,
          Text,
          OriginPagePath: window.location.pathname,
          OriginPageTitle: document.title,
        });
      if (!result.Success) {
        setMessages((current) => current.filter((message) => message !== temporaryMessage));
        return;
      }
      if (!session && result.Result) {
        sessionStorage.setItem(key, JSON.stringify(result.Result));
        setSessionVersion((version) => version + 1);
      }
      await load();
    } catch {
      setMessages((current) => current.filter((message) => message !== temporaryMessage));
    } finally {
      setSending(false);
    }
  };

  return <>
    <Badge count={unreadCount} size="small"><FloatButton shape="square" icon={<MessageOutlined />} content={t("landing.conversation.title", undefined, "Support chat")} tooltip={t("landing.conversation.title", undefined, "Support chat")} onClick={() => { setUnreadCount(0); setOpen(true); }} style={{ insetInlineEnd: 24, insetBlockEnd: 24, zIndex: 2100 }} /></Badge>
    {open && <Card
      size="small"
      title={<Flex align="center" gap={10}>
        <Badge dot color={token.colorSuccess} offset={[-2, 30]}>
          <Avatar size={38} icon={<CustomerServiceOutlined />} style={{ background: token.colorPrimary }} />
        </Badge>
        <Flex vertical gap={0}>
          <Typography.Text strong>{operatorName ?? t("landing.conversation.title", undefined, "Support chat")}</Typography.Text>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}><CheckCircleFilled style={{ color: token.colorSuccess, marginInlineEnd: 4 }} />{t("landing.conversation.title", undefined, "Support chat")}</Typography.Text>
        </Flex>
      </Flex>}
      extra={<Button type="text" size="small" icon={<CloseOutlined />} onClick={() => setOpen(false)} />}
      styles={{ body: { height: "min(60vh, 520px)", padding: 12, display: "flex", flexDirection: "column", background: token.colorFillQuaternary } }}
      style={{ position: "fixed", insetInlineEnd: 24, bottom: 90, zIndex: 2100, width: "min(calc(100vw - 32px), 380px)", boxShadow: token.boxShadowSecondary, overflow: "hidden" }}
    >
      <div style={{ flex: 1, overflowY: "auto", paddingInline: 4 }}>
        {messages.length === 0 ? <Flex align="center" justify="center" style={{ height: "100%" }}><Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={false} /></Flex> : <List split={false} dataSource={messages} renderItem={(message) => {
          const isVisitor = message.SenderName === "Visitor";
          return <List.Item style={{ justifyContent: isVisitor ? "flex-end" : "flex-start", padding: "4px 0", border: 0 }}>
            <Flex vertical gap={3} style={{ maxWidth: "82%", padding: "9px 11px", borderRadius: isVisitor ? "16px 4px 16px 16px" : "4px 16px 16px 16px", background: isVisitor ? token.colorPrimary : token.colorBgContainer, color: isVisitor ? token.colorTextLightSolid : token.colorText, boxShadow: token.boxShadowTertiary }}>
              {!isVisitor && <Typography.Text strong style={{ color: "inherit", fontSize: 12 }}>{message.SenderName}</Typography.Text>}
              <Typography.Text style={{ color: "inherit", whiteSpace: "pre-wrap" }}>{message.Text}</Typography.Text>
              {message.DateRegister && <Typography.Text style={{ color: "inherit", opacity: 0.68, fontSize: 11, alignSelf: "flex-end" }}><Format type="DateTime" value={message.DateRegister} /></Typography.Text>}
            </Flex>
          </List.Item>;
        }} />}
        <div ref={MessagesEndRef} />
      </div>
      <Form form={form} onFinish={send} style={{ marginTop: 10 }}>
        <Flex gap={8} align="end">
          <Form.Item name="Text" rules={[{ required: true, max: 4000 }]} style={{ flex: 1, marginBottom: 0 }}><Input.TextArea autoSize={{ minRows: 1, maxRows: 4 }} maxLength={4000} onPressEnter={(event) => {
            if (!event.shiftKey && !event.ctrlKey && !event.metaKey && !event.nativeEvent.isComposing) {
              event.preventDefault();
              form.submit();
            }
          }} /></Form.Item>
          <Button htmlType="submit" type="primary" loading={sending} icon={<SendOutlined />} />
        </Flex>
      </Form>
    </Card>}
  </>;
}
